/**
 * Device Tracking Utilities
 * GDPR-compliant device fingerprinting for customer analytics
 */

import { v4 as uuidv4 } from 'uuid';

const DEVICE_ID_KEY = 'qorder_device_id';
const CONSENT_KEY = 'qorder_tracking_consent';
const CUSTOMER_EMAIL_KEY = 'qorder_customer_email';
const CUSTOMER_PHONE_KEY = 'qorder_customer_phone';

export interface TrackingConsent {
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
}

export interface CustomerData {
  deviceId: string;
  email?: string;
  phone?: string;
  hasConsent: boolean;
}

/**
 * Check if user has given tracking consent
 */
export function hasTrackingConsent(): boolean {
  try {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) return false;

    const parsed: TrackingConsent = JSON.parse(consent);
    return parsed.analytics === true;
  } catch {
    return false;
  }
}

/**
 * Save tracking consent
 */
export function saveTrackingConsent(analytics: boolean, marketing: boolean): void {
  const consent: TrackingConsent = {
    analytics,
    marketing,
    timestamp: new Date().toISOString(),
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
}

/**
 * Get or create device ID
 * Only returns ID if user has given consent
 */
export function getOrCreateDeviceId(): string | null {
  if (!hasTrackingConsent()) {
    return null;
  }

  try {
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);

    if (!deviceId) {
      deviceId = uuidv4();
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }

    return deviceId;
  } catch (error) {
    console.error('Failed to get/create device ID:', error);
    return null;
  }
}

/**
 * Save customer contact info (for loyalty program)
 */
export function saveCustomerContact(email?: string, phone?: string): void {
  try {
    if (email) {
      localStorage.setItem(CUSTOMER_EMAIL_KEY, email);
    }
    if (phone) {
      localStorage.setItem(CUSTOMER_PHONE_KEY, phone);
    }
  } catch (error) {
    console.error('Failed to save customer contact:', error);
  }
}

/**
 * Get saved customer contact info
 */
export function getCustomerContact(): { email?: string; phone?: string } {
  try {
    const email = localStorage.getItem(CUSTOMER_EMAIL_KEY) || undefined;
    const phone = localStorage.getItem(CUSTOMER_PHONE_KEY) || undefined;
    return { email, phone };
  } catch {
    return {};
  }
}

/**
 * Get complete customer data for orders
 */
export function getCustomerData(): CustomerData {
  const hasConsent = hasTrackingConsent();
  const deviceId = hasConsent ? getOrCreateDeviceId() : uuidv4(); // Fallback to anonymous ID
  const contact = getCustomerContact();

  return {
    deviceId: deviceId || uuidv4(),
    email: contact.email,
    phone: contact.phone,
    hasConsent,
  };
}

/**
 * Clear all tracking data (for GDPR right to be forgotten)
 */
export function clearTrackingData(): void {
  try {
    localStorage.removeItem(DEVICE_ID_KEY);
    localStorage.removeItem(CONSENT_KEY);
    localStorage.removeItem(CUSTOMER_EMAIL_KEY);
    localStorage.removeItem(CUSTOMER_PHONE_KEY);
  } catch (error) {
    console.error('Failed to clear tracking data:', error);
  }
}

/**
 * Get marketing consent status
 */
export function hasMarketingConsent(): boolean {
  try {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) return false;

    const parsed: TrackingConsent = JSON.parse(consent);
    return parsed.marketing === true;
  } catch {
    return false;
  }
}
